import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { Tooltip } from '../../components/overlays/Tooltip';
import { useTheme, useThemeColors } from '../../providers/ThemeProvider';
import { cn } from '../../utils/cn';

export type CallControlButtonTone = 'default' | 'selected' | 'muted' | 'held';

export interface CallControlButtonFaceProps
	extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
	icon: IconDefinition;
	/** Visible caption under the icon, already translated. */
	caption: string;
	tone?: CallControlButtonTone;
	/** 'lg' is the phone size, a 44px circle for a thumb. */
	size?: 'md' | 'lg';
	/**
	 * Rendered as `aria-disabled` rather than `disabled`, so a tap on a phone
	 * still reaches the tooltip that says why the control is unavailable. The
	 * click handler is not called while this is set.
	 */
	unavailable?: boolean;
	/** Live mic input 0..1, drawn as a fill rising inside the circle. */
	micLevel?: number | null;
}

/**
 * One round in-call control: an icon in a circle with its caption underneath
 * (PD-14951). The caption is always visible, because on a phone there is no
 * hover and an icon alone did not say what the button does.
 *
 * Forwards its ref and spreads the remaining props onto the `<button>`, so it
 * can be a Headless UI menu trigger (the More button) as well as a plain
 * control.
 */
export const CallControlButtonFace = React.forwardRef<
	HTMLButtonElement,
	CallControlButtonFaceProps
>(function CallControlButtonFace(
	{
		icon,
		caption,
		tone = 'default',
		size = 'md',
		unavailable = false,
		micLevel = null,
		className,
		onClick,
		...buttonProps
	},
	ref,
) {
	const colors = useThemeColors();
	const { isDark } = useTheme();
	const toneClasses: Record<CallControlButtonTone, string> = {
		default: cn(colors.classes.bgTertiary, colors.classes.textPrimary),
		selected: colors.classes.controlSelected,
		muted: colors.classes.badgeWarning,
		held: cn(colors.classes.bgWarningSolid, 'text-on-warning'),
	};
	return (
		<button
			ref={ref}
			type="button"
			{...buttonProps}
			aria-disabled={unavailable ? true : undefined}
			onClick={(event) => {
				if (unavailable) {
					return;
				}
				onClick?.(event);
			}}
			className={cn(
				// focus-visible rather than the focusRingPrimary token: a ring left
				// on Mute after every mouse click reads as a stuck state.
				'group flex flex-col items-center gap-1 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-accent',
				unavailable && 'cursor-not-allowed',
				className,
			)}
		>
			<span
				className={cn(
					'relative flex items-center justify-center overflow-hidden rounded-full transition-colors group-hover:opacity-80',
					size === 'lg' ? 'h-11 w-11' : 'h-10 w-10',
					toneClasses[tone],
					unavailable && 'opacity-50 group-hover:opacity-50',
				)}
			>
				{micLevel != null ? (
					<MicLevelFill
						level={micLevel}
						isDark={isDark}
						tone={
							tone === 'muted' ? colors.values.warning : colors.values.success
						}
					/>
				) : null}
				<FontAwesomeIcon icon={icon} className="relative" aria-hidden />
			</span>
			<span
				className={cn(
					'whitespace-nowrap text-[0.6875rem] font-medium leading-none',
					colors.classes.textSecondary,
					unavailable && 'opacity-60',
				)}
			>
				{caption}
			</span>
		</button>
	);
});

export interface CallControlButtonProps extends CallControlButtonFaceProps {
	/** Extra detail on hover, already translated. */
	tooltip?: string | null;
	/** Why the control is unavailable, already translated. Shown instead of
	 *  `tooltip` while `unavailable` is set, on every layout. */
	unavailableReason?: string | null;
	/**
	 * Whether the detail tooltip shows while the control is usable. Off on a
	 * phone: there a tap is the action, and a tip popping up on every tap of
	 * Mute is noise when the caption already says what it does. The reason a
	 * control is unavailable still shows, since that tap does nothing else.
	 */
	showDetailTooltip?: boolean;
}

/** A `CallControlButtonFace` wrapped in the shared Tooltip. */
export function CallControlButton({
	tooltip = null,
	unavailableReason = null,
	showDetailTooltip = true,
	...faceProps
}: CallControlButtonProps): React.JSX.Element {
	const content =
		faceProps.unavailable === true
			? unavailableReason
			: showDetailTooltip
				? tooltip
				: null;
	return (
		<Tooltip
			content={content ?? ''}
			disabled={content == null || content === ''}
			position="top"
			className="inline-flex"
			maxWidth="260px"
		>
			<CallControlButtonFace {...faceProps} />
		</Tooltip>
	);
}

/**
 * Subtle vertical fill behind the mute icon reflecting current mic input RMS.
 * Linear scaling 0..1 → 0..100% height. Deliberately not a full VU meter — the
 * goal is "is my mic working?" reassurance, not studio-grade metering.
 */
function MicLevelFill({
	level,
	tone,
	isDark,
}: {
	level: number;
	tone: string;
	isDark: boolean;
}): React.JSX.Element {
	const heightPct = Math.round(Math.min(1, Math.max(0, level)) * 100);
	return (
		<span
			aria-hidden
			className="absolute inset-x-0 bottom-0 transition-[height] duration-75 ease-out"
			style={{
				height: `${heightPct}%`,
				backgroundColor: tone,
				// A quarter-strength tint all but vanishes on the dark surface.
				opacity: isDark ? 0.45 : 0.25,
			}}
		/>
	);
}
